/**
 * @author EdenCha <eden@nurigo.net>
 */
import React, { Component } from 'react'
import autoBind from 'auto-bind'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import moment from 'moment-timezone'
import qs from 'qs'
import copy from 'copy-to-clipboard'

import isEqualProps from 'lib/isEqualProps'
import bindStateToProps from 'lib/bindStateToProps'
import bindActionCreators from 'lib/bindActionCreators'
import mapActionLog from 'lib/mapActionLog'
import mapAsyncActions from 'lib/mapAsyncActions'

// redux modules (action)
import { actions as rbcActions } from 'store/modules/rbc'
import { actions as confirmActions } from 'store/modules/confirm'
import { actions as searchActions } from 'store/modules/search'
import { actions as tableActions } from 'store/modules/table'
import { actions as popupActions } from 'store/modules/popup'
import { actions as snackbarActions } from 'store/modules/snackbar'

import config from 'config'
import { isArray } from 'lib/detectType'
import Typography from '@material-ui/core/Typography'
import SimpleCopy from 'components/atoms/SimpleCopy'
import RcsBrandSelectForm from 'components/molecules/RcsBrandSelectForm'

/**
 * (작성된 설명이 없습니다.)
 */
const hoc = (
  WrappedComponent,
  { mapStateToProps = {}, useActions = {}, options: { isAdmin } = {} } = {}
) => {
  class RcsTemplateListHoc extends Component {
    constructor(props) {
      super(props)
      autoBind(this)
      mapActionLog(this)
      // 2차 인증을 사용하는 리덕스 액션이 존재하는 경우 덮어씌워 await이 정상적으로 작동하게 합니다.
      mapAsyncActions(this.props, {
        // exceptActions: { AppsActions: true }
      })
      this.state = {}
    }

    shouldComponentUpdate = (nextProps, nextState) => {
      return (
        !isEqualProps(nextProps, this.props) ||
        !isEqualProps(nextState, this.state, ['brandList'])
      )
    }

    // hoc 사용 시 해당 부분 삭제하세요.
    componentDidMount = () => {
      this.initTemplateSearch()
    }

    getRcsBrandList = async () => {
      const { RbcActions } = this.props
      if (isArray(this.state.brandList)) return this.state.brandList
      const { data: { brandList = [] } = {} } = await RbcActions.getBrandList({
        limit: 1000
      })
      return brandList
    }

    initTemplateSearch = async () => {
      const { SearchActions } = this.props
      const brandList = await this.getRcsBrandList()
      const key = 'rcsTemplates'
      const brandIds = brandList.map(data => {
        return {
          label: data?.name,
          value: data?.brandId
        }
      })
      SearchActions.init({
        key,
        options: {
          simpleForm: true,
          onInitCallback: this.initTable,
          onSearchCallback: this.initTable
        },
        value: qs.parse(window.location.search.replace(/^./, '')),
        data: {
          brandId: {
            label: '브랜드 선택',
            type: 'select',
            value: [{ label: '전체 브랜드', value: '' }].concat(brandIds),
            defaultValue: '',
            gridItemProps: { style: { minWidth: 200 } }
          },
          name: {
            label: '템플릿 이름',
            type: 'text',
            simpleFormOperator: 'like'
          },
          templateId: {
            label: '템플릿 ID',
            type: 'text',
            gridItemProps: { style: { minWidth: 250 } }
          }
        }
      })
    }

    searchDateFormat = result => {
      const { startDate, endDate, ...others } = result
      return {
        ...others,
        dateUpdated: {
          gte: startDate,
          lte: endDate
        }
      }
    }

    initTable = async (searchResult = {}) => {
      const { RbcActions, TableActions } = this.props
      const rowsPerPage = 20
      const tableData = []
      const params = {
        limit: rowsPerPage,
        ...this.searchDateFormat(searchResult)
      }
      try {
        const {
          data: { templateList = [] }
        } = await RbcActions.getTemplateList(params)
        tableData.push(...templateList)
      } catch (error) {}
      TableActions.init({
        key: 'rcsTemplates',
        options: {
          materialList: false,
          title: 'RCS 템플릿 목록',
          rowsPerPage,
          rowHighlight: true,
          formatDataCallback: this.makeTableData,
          extraButton: this.makeExtraButton,
          onLoadCallback: async (limit, offset, hash) => {
            const { getTemplateList = {} } = this.props
            const { nextKey: startKey } = getTemplateList?.data || {}
            try {
              if (!startKey) throw new Error()
              const {
                data: { templateList = [] }
              } = await RbcActions.getTemplateList({
                startKey,
                limit,
                ...params
              })
              return TableActions.add({
                key: 'rcsTemplates',
                limit,
                data: templateList,
                hash
              })
            } catch (error) {}
          }
        },
        columns: {
          name: { primary: '템플릿 이름' },
          templateId: { primary: '템플릿 아이디' },
          cardType: { primary: '형태' },
          status: { primary: '상태' },
          dateUpdated: { primary: '최근 수정일' }
        },
        data: tableData
      })
    }

    makeExtraButton = (data, absIndex) => {
      return {
        columnName: '템플릿 관리',
        group: true,
        buttonList: [
          {
            name: '템플릿 정보',
            newTab: true,
            buttonProps: { style: { minWidth: 60 } },
            onClick: () => {
              window.open(
                `https://www.rcsbizcenter.com/template/list/${data?.brandId}/${data?.templateId}`,
                '_blank'
              )
            }
          },
          {
            name: '템플릿 ID 복사',
            onClick: () => {
              copy(data?.templateId)
            }
          },
          {
            name: '템플릿 삭제',
            onClick: () => this.onClickDeleteTemplate(data?.templateId)
          }
        ]
      }
    }

    makeTableData = rawData => {
      return rawData.map(data => {
        return {
          ...data,
          name: (
            <Typography
              title={data?.name}
              variant="body2"
              color={data?.isHidden ? 'textSecondary' : 'textPrimary'}
              style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
                minWidth: 50,
                maxWidth: 200,
                overflow: 'hidden'
              }}
              noWrap
            >
              {data?.name}
            </Typography>
          ),
          templateId: (
            <SimpleCopy title="템플릿 아이디" copyText={data?.templateId}>
              <Typography variant="caption">{data?.templateId}</Typography>
            </SimpleCopy>
          ),
          status: config?.rcsTemplateStatus[data?.status] || data?.status,
          cardType: config?.rcsCardType[data?.cardType] || data?.cardType,
          dateUpdated: moment(data?.dateUpdated).format('YYYY.MM.DD')
        }
      })
    }

    onClickDeleteTemplate = templateId => {
      const { ConfirmActions, RbcActions, searchResult = {} } = this.props
      ConfirmActions.show({
        key: 'public',
        message: '정말로 템플릿을 삭제합니까?',
        onConfirm: async () => {
          await RbcActions.deleteTemplate(
            { templateId },
            { successMessage: '템플릿을 삭제했습니다.' }
          )
          this.initTable(searchResult)
        }
      })
    }

    onClickCreateTemplate = async () => {
      const { ConfirmActions, PopupActions } = this.props
      const brandList = await this.getRcsBrandList()
      PopupActions.new({
        message: (
          <RcsBrandSelectForm
            brandList={brandList}
            onSelect={rcsBrand => {
              PopupActions.close()
              ConfirmActions.show({
                severity: 'info',
                popupMessage: true,
                message: `새 창에서 RCS 비즈 센터 페이지를 엽니다. 승인 완료 후 가져오기가 필요합니다.`,
                onConfirm: () => {
                  window.open(
                    `https://www.rcsbizcenter.com/template/create/${rcsBrand?.brandId}/list`
                  )
                }
              })
            }}
          />
        )
      })
    }

    onClickSyncTemplate = async () => {
      const { ConfirmActions, RbcActions, SnackbarActions } = this.props
      const brandList = await this.getRcsBrandList()
      const brandIds = brandList.map(data => data?.brandId)
      ConfirmActions.show({
        severity: 'info',
        message: 'RCS 비즈페이지에서 "승인이 완료된 템플릿"만 가져옵니다.',
        onConfirm: () => {
          Promise.all(
            brandIds.map(brandId => RbcActions.syncTemplate({ brandId }))
          ).then(() => {
            this.initTemplateSearch()
            SnackbarActions.new({
              severity: 'success',
              message: 'RCS 템플릿 동기화를 완료했습니다. '
            })
          })
        }
      })
    }

    render = () => <WrappedComponent {...this.props} proxy={this} />
  }
  return connect(
    bindStateToProps({
      rbc: [
        {
          getBrandList: 'responseData.getBrandList.data',
          getTemplateList: 'responseData.getTemplateList'
        }
      ],
      search: [{ searchResult: 'result.rcsTemplates' }],
      ...mapStateToProps
    }),
    bindActionCreators({
      snackbarActions,
      searchActions,
      confirmActions,
      rbcActions,
      tableActions,
      popupActions,
      ...useActions
    })
  )(withRouter(RcsTemplateListHoc))
}
export default hoc
